import { useState, useEffect, useCallback } from "react";
import { Box, Container, Text, Paper, Loader, Center, Button, Stack, Flex, Title, Divider } from "@mantine/core";
import { IconArrowLeft, IconSparkles, IconWallet } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useUserContext } from "@/context/UserContext";

interface WalletTransaction {
  id: string;
  companyReferenceId: string;
  date: string;
  amount: number;
  currency: string;
}

interface WalletStats {
  availableCredits: number;
  totalCreditsEarned: number;
  creditsUsed: number;
}

export default function WalletPage() {
  const navigate = useNavigate();
  const { userDetails, hasProfile } = useUserContext();
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [stats, setStats] = useState<WalletStats>({
    availableCredits: 0,
    totalCreditsEarned: 0,
    creditsUsed: 0,
  });
  const [loading, setLoading] = useState(true);

  const loadWalletData = useCallback(async () => {
    try {
      if (hasProfile === null) return;
      setLoading(true);
      if (userDetails?.company?.id) {
        // Fetch transactions and balance concurrently
        const [ledgerData, balanceData] = await Promise.all([
          trpc.wallet.getTransactions.query({ filter: "WALLET" }),
          trpc.wallet.getBalance.query(),
        ]);

        // Set stats from optimized backend calculation
        setStats({
          availableCredits: balanceData.available,
          totalCreditsEarned: balanceData.totalEarned,
          creditsUsed: balanceData.totalSpent,
        });

        // Transform to wallet transaction format (data is already filtered by backend)
        const formattedTransactions: WalletTransaction[] = ledgerData.map((entry: any) => ({
          id: entry.id,
          companyReferenceId: entry.matchedCompanyGtid || "N/A",
          date: new Date(entry.createdAt).toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
          }),
          amount: entry.amount,
          currency: entry.currency,
        }));

        setTransactions(formattedTransactions);
      }
    } catch (error) {
      handleTrpcError(error);
    } finally {
      setLoading(false);
    }
  }, [hasProfile, userDetails?.company?.id]);

  useEffect(() => {
    loadWalletData();
  }, [loadWalletData]);

  const handleExploreCompanies = () => {
    navigate("/my-discovery");
  };

  if (loading) {
    return (
      <Box mih="100vh" pb={20}>
        <Container size="xl" mt="xl">
          <Center h={300}>
            <Loader size="lg" />
          </Center>
        </Container>
      </Box>
    );
  }

  return (
    <Box mih="100vh" pb={20}>
      <Container size="fluid" mt="md">
        {/* Back Button */}
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={18} />}
          onClick={() => navigate("/invoices")}
          mb="md"
          px="xs"
        >
          Back to Invoices & Payments
        </Button>

        {/* Title */}
        <Title order={2} size="h2" mb="xl" fz={28} fw={400}>
          GT Wallet
        </Title>

        {/* Stats Card */}
        <Paper p="xl" radius="md" withBorder mb="xl">
          <Flex gap="xl" wrap="wrap">
            {/* Available Credits */}
            <Box style={{ flex: "1 1 300px" }}>
              <Flex align="center" gap="lg" mb="md">
                <Box
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 12,
                    background: "var(--mantine-color-primary-0)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconWallet size={28} color="var(--mantine-color-primary-7)" />
                </Box>
                <Text fz={42} fw={400} lh={1} c="dark.9">
                  {stats.availableCredits}
                </Text>
              </Flex>
              <Text size="md" fw={400} c="dark.9">
                Available Collaboration Credits
              </Text>
              <Text size="sm" c="slate.6" mt={4}>
                Credits can be used for future collaborations
              </Text>
            </Box>

            <Divider orientation="vertical" style={{ alignSelf: "stretch" }} />

            {/* Right Side Stack */}
            <Stack gap="xl" style={{ flex: "1 1 200px" }}>
              {/* Total Credits Earned */}
              <Box>
                <Text fz={22} fw={400} lh={1} c="dark.9">
                  {stats.totalCreditsEarned}
                </Text>
                <Text size="sm" fw={400} c="slate.6" mt="xs">
                  Total Credits Earned
                </Text>
              </Box>

              <Divider />

              {/* Credits Used */}
              <Box>
                <Text fz={22} fw={400} lh={1} c="dark.9">
                  {stats.creditsUsed}
                </Text>
                <Text size="sm" fw={400} c="slate.6" mt="xs">
                  Credits Used
                </Text>
              </Box>
            </Stack>
          </Flex>
        </Paper>

        {/* Info Box */}
        <Paper
          p="lg"
          radius="md"
          mb="xl"
          style={{
            background: "var(--mantine-color-primary-0)",
            border: "1px solid var(--mantine-color-primary-2)",
          }}
        >
          <Flex justify="space-between" align="center" wrap="wrap" gap="md">
            <Flex align="center" gap="md">
              <Box
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: "var(--mantine-color-primary-2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconSparkles size={24} color="var(--mantine-color-primary-7)" />
              </Box>
              <Box>
                <Text size="sm" fw={400} c="dark.9">
                  Use Your Credits
                </Text>
                <Text size="xs" c="slate.5">
                  Credits will be automatically applied during collaboration checkout
                </Text>
              </Box>
            </Flex>
            <Button variant="filled" color="primary" onClick={handleExploreCompanies}>
              Explore Companies
            </Button>
          </Flex>
        </Paper>

        {/* Transactions List */}
        {transactions.length === 0 ? (
          <Paper p="xl" radius="lg" withBorder>
            <Stack align="center" gap="md" py="xl">
              <IconWallet size={48} color="var(--mantine-color-gray-5)" />
              <Text size="lg" fw={400} c="slate.5">
                No wallet credits yet
              </Text>
              <Text size="sm" c="slate.5" ta="center">
                Your wallet credit history will appear here once you receive credits.
              </Text>
            </Stack>
          </Paper>
        ) : (
          <Stack gap="md">
            {transactions.map((transaction) => (
              <Paper key={transaction.id} p="md" radius="md" withBorder>
                <Flex justify="space-between" align="center">
                  <Text size="sm" fw={600} c="dark.9">
                    {transaction.companyReferenceId}
                  </Text>
                  <Text size="sm" c="slate.6">
                    {transaction.date}
                  </Text>
                </Flex>
              </Paper>
            ))}
          </Stack>
        )}
      </Container>
    </Box>
  );
}
